import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import apiClient from '../api/client';
import { Run } from '../types';
import TrainingRunDetails from '../components/TrainingRunDetails';

const RunDetails: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [run, setRun] = useState<Run | null>(null);
  const [loading, setLoading] = useState(true);

  const runRef = useRef(run);
  runRef.current = run;

  useEffect(() => {
    const loadData = async () => {
      try {
        const runRes = await apiClient.get(`/api/v1/runs/${slug}`);
        setRun(runRes.data);
        setLoading(false);
      } catch (error) {
        console.error('Failed to load run details:', error);
        setLoading(false);
      }
    };

    loadData();
    // Only poll while run is active
    const interval = setInterval(() => {
      if (runRef.current && (runRef.current.status === 'completed' || runRef.current.status === 'failed' || runRef.current.status === 'paused')) return;
      loadData();
    }, 5000);
    return () => clearInterval(interval);
  }, [slug]);

  if (loading || !run) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  const isTrainingMode = run.config.mode === 'training';

  return (
    <Box sx={{ p: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <IconButton onClick={() => navigate('/runs')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 600 }}>
            {run.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {isTrainingMode
              ? `${run.num_tasks} training task${run.num_tasks > 1 ? 's' : ''}`
              : 'Legacy run'}
          </Typography>
        </Box>
      </Box>

      {isTrainingMode ? (
        <TrainingRunDetails run={run} />
      ) : (
        <Paper
          sx={{
            p: 4,
            backgroundColor: '#0a0a0a',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            textAlign: 'center',
          }}
        >
          <Typography variant="body1" color="text.secondary">
            Legacy Run — older format no longer supported
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default RunDetails;
